import { z } from 'zod';
/**
 * Trips — the frame an incident hangs on.
 * Design: `docs/architecture/trip-context-design.md`.
 *
 * DERIVED from telemetry OIP already holds, never ingested. A provider's trip
 * arrives pre-computed with its own semantics baked in — how long an idle
 * splits a journey, whether idling counts as trip time — so Wialon's trip and
 * Webfleet's trip are different objects with the same name, and putting both in
 * one table produces numbers that look comparable and are not.
 *
 * There is deliberately **no trip list page, no live view and no map replay**.
 * The customer already has those on the provider's home ground, where a copy
 * competes rather than complements and then drifts. What no provider can show
 * them is a trip with an incident in it, because the provider has no incidents.
 */
export const TRIP_END_REASON_LABELS = {
    IGNITION_OFF: 'ignition switched off',
    IDLE: 'stationary long enough to end the trip',
    /*
     * Named as an ABSENCE rather than as an ending. The device stopped reporting;
     * whether the vehicle stopped is not something the platform observed, and a
     * label like "trip ended" would assert it did.
     */
    GAP: 'telemetry stopped — the trip was closed at the last sample',
};
export const TRIP_DISTANCE_SOURCE_LABELS = {
    /** What the vehicle counted. */
    ODOMETER: 'from the odometer',
    /*
     * A polyline through the samples that happened to arrive. At a 30-second
     * interval it cuts every corner and under-reports, so the screen says which
     * one produced the number rather than presenting both as one measurement.
     */
    POSITIONS: 'estimated from reported positions',
    NONE: 'not measured',
};
export const tripSchema = z.object({
    id: z.string().uuid(),
    vehicleId: z.string().uuid(),
    startedAt: z.string(),
    /** Null while the trip is still in progress. */
    endedAt: z.string().nullable(),
    startReason: z.enum(['IGNITION_ON', 'MOTION']),
    endReason: z.enum(['IGNITION_OFF', 'IDLE', 'GAP']).nullable(),
    durationSeconds: z.number().int().nullable(),
    /**
     * Null when nothing measured it — NOT zero.
     *
     * A distance of zero is a vehicle that did not move; an unmeasured distance is
     * a vehicle the platform cannot speak for, and the two must not render alike.
     */
    distanceMetres: z.number().int().nullable(),
    distanceSource: z.enum(['ODOMETER', 'POSITIONS', 'NONE']),
    startLatitude: z.number().nullable(),
    startLongitude: z.number().nullable(),
    endLatitude: z.number().nullable(),
    endLongitude: z.number().nullable(),
    /** Telemetry samples behind it. A trip from four samples is a weaker claim. */
    sampleCount: z.number().int(),
    /** Incidents whose events fell inside this trip. The reason trips are shown. */
    incidentCount: z.number().int(),
});
/**
 * Why there is no trip, when there is no trip (D8).
 *
 * Three different absences that must not look alike — a blank space would read
 * as a platform failure in all three, and only one of them is even a problem.
 * The same rule as `samplesRead` on the vehicle map, where "reported no
 * position" and "reported nothing" draw the same empty map and mean opposite
 * things.
 */
export const tripAbsenceSchema = z.enum([
    /** The tenant cannot send telemetry at all. An entitlement fact. */
    'NOT_ENTITLED',
    /** The vehicle reports neither ignition nor speed, so it gets no trips (D2). */
    'VEHICLE_REPORTS_NO_SIGNAL',
    /** It genuinely fell outside every trip: a tow, a break-in, a parked alarm. */
    'OUTSIDE_ANY_TRIP',
]);
export const TRIP_ABSENCE_WORDING = {
    NOT_ENTITLED: 'Trip context needs the Telemetry entitlement.',
    VEHICLE_REPORTS_NO_SIGNAL: 'This vehicle does not report the ignition or speed a trip is derived from.',
    OUTSIDE_ANY_TRIP: 'Not during a trip.',
};
/** A trip on an incident, or the reason there is none. Never both, never neither. */
export const incidentTripSchema = z.object({
    trip: tripSchema.nullable(),
    absence: tripAbsenceSchema.nullable(),
    /** How far into the trip the incident's first event fell. Null with no trip. */
    elapsedMinutes: z.number().nullable(),
});
export const vehicleTripsQuerySchema = z
    .object({
    /** Window in hours, back from now. A day by default — the vehicle page's list. */
    hours: z.coerce
        .number()
        .int()
        .positive()
        .max(24 * 30)
        .default(24),
    /**
     * One whole UTC day, instead of a window back from now.
     *
     * ⚠️ **Added so a finding can be opened ON THE DAY IT HAPPENED.** Four of the
     * six anomaly families are read off trips, so a link from a two-day-old
     * distance anomaly landing on "the last 24 hours" would show none of the
     * evidence the finding rests on.
     *
     * ⚠️ It OVERRIDES `hours`, and it is deliberately UNBOUNDED where `hours` is
     * capped at 30 days. That cap bounds how much a single request may scan; one
     * day is one day's work whenever it was. And a `Trip` is never pruned — it is
     * precisely *what outlives the samples*, so unlike the state timeline there
     * is no retention boundary here to refuse at. A baseline window is 90 days,
     * so a finding is routinely older than 30 and refusing it would leave the
     * commonest anomaly families with no evidence to open.
     */
    day: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD')
        .optional(),
})
    .strict();
export const vehicleTripsResponseSchema = z.object({
    vehicleId: z.string().uuid(),
    from: z.string(),
    to: z.string(),
    items: z.array(tripSchema),
    /**
     * Set when the list is empty for a REASON rather than because the vehicle sat
     * still. Null when there are trips, or when the window simply holds none.
     */
    absence: tripAbsenceSchema.nullable(),
});
//# sourceMappingURL=trip.js.map